import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {Control, Disposers, StringControl} from '../../../../../../lib/helpers';
import {McqBehavior, McqChoice, McqModel} from '../../models';

export class McqChoiceStore {
  readonly label = new StringControl('', '');
  readonly selected = new Control<boolean>('', false);
}

export class McqEditStore {
  readonly behavior: Control<McqBehavior>;

  @observable.shallow
  readonly choices: McqChoiceStore[];

  readonly disposers = new Disposers();

  constructor() {
    makeObservable(this);
    this.behavior = new Control<McqBehavior>('', 'single-select');
    this.choices = [];
    for (let index = 0; index < 5; index++)
      this.addChoice();
    this.setup();
  }

  @action
  sync = (model: McqModel) => {
    this.choices.length = 0;

    this.behavior.setValue(model.behavior);

    for (let index = 0; index < model.choices.length; index++) {
      this.addChoice();
      let choice = model.choices[index];
      const choiceStore = this.choices[index];
      choiceStore.label.setValue(choice.label);
      choiceStore.selected.setValue(choice.selected);
    }
  };

  @action
  addChoice = () => {
    const store = new McqChoiceStore();
    this.choices.push(store);
  };

  @action
  removeChoice = (index: number) => {
    this.choices.splice(index, 1);
  };

  @computed
  get valid(): boolean {
    if (this.behavior.value === 'single-select') {
      let notCheckedOptionFound = false;
      let checkedOptionFound = false;
      for (let option of this.choices) {
        if (option.selected.value && option.label.value.trim() !== '')
          checkedOptionFound = true;
        if (!option.selected.value && option.label.value.trim() !== '')
          notCheckedOptionFound = true;
        if (checkedOptionFound && notCheckedOptionFound)
          return true;
      }
      return false;
    }
    if (this.behavior.value === 'multi-select') {
      for (let option of this.choices)
        if (option.label.value.trim() !== '')
          return true;
      return false;
    }
    return false;
  }

  @computed
  get x9Contents(): string {
    if (!this.valid)
      return '';

    const options: McqChoice[] = [];

    for (let option of this.choices) {
      const label = option.label.value.trim();
      if (label !== '')
        options.push({label: label, selected: option.selected.value});
    }

    const result: McqModel = {
      choices: options,
      behavior: this.behavior.value
    };
    return JSON.stringify(result);
  };

  @action
  toggleChoice(index: number) {
    const choiceStore = this.choices[index];

    if (this.behavior.value === 'single-select' && !choiceStore.selected.value) {
      // uncheck other choices
      this.choices.forEach(x => x.selected.setValue(false));
      choiceStore.selected.setValue(true);
      return;
    }
    choiceStore.selected.setValue(!choiceStore.selected.value);
  }

  private setup() {
    this.disposers.add(reaction(
      () => this.behavior.value,
      this.onBehaviorChange
    ));
  }

  @action
  private onBehaviorChange = () => {
    if (this.behavior.value !== 'single-select')
      return;
    let checkedOptionFound = false;
    for (let choice of this.choices) {
      choice.selected.setValue(choice.selected.value && !checkedOptionFound);
      if (choice.selected.value)
        checkedOptionFound = true;
    }
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
